import type { CityConfig } from "../city-template/types";

export const providenceConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Providence",
  state: "Rhode Island",
  stateAbbr: "RI",
  slug: "providence",
  route: "/providence",
  title: "Emergency Passport Providence, RI — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Providence, RI — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Providence business travelers, Brown University and RISD students, state government employees, and Rhode Island families.",
  h1: "Emergency & Expedited Passport Services in Providence, RI",
  heroSubtitle: "Your passport expired and RISD's study-abroad program starts in Rome next week? State House business taking you to Brussels for an EU summit? Brown student heading to study in London this semester? We help Providence residents — from College Hill academics to Federal Hill families to downtown professionals — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20panoramic%20view%20of%20downtown%20Providence%20Rhode%20Island%20at%20twilight%20with%20the%20iconic%20marble%20State%20House%20dome%20glowing%20warmly%20against%20the%20darkening%20sky%2C%20WaterFire%20braziers%20flickering%20along%20the%20three%20rivers%20reflecting%20golden%20light%2C%20historic%20brick%20architecture%20and%20modern%20skyline%20together%2C%20elevated%20cityscape%20photography%20with%20rich%20amber%20tones%20and%20dramatic%20clouds%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=providence-hero-2026&orientation=landscape",
  heroImageAlt: "Providence Rhode Island skyline with State House dome at twilight with WaterFire rivers",
  geoPosition: "41.8240;-71.4128",
  geoPlacename: "Providence, Rhode Island",
  lastModified: "2026-06-12",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "10 Causeway St, Boston, MA 02222",
    distance: "Located in downtown Boston — approximately 50 miles from Providence, appointments required, typically booked 2+ weeks out",
  },
  nearbyCitiesZips: [
    { name: "Providence", zips: "02901–02912, 02918, 02940" },
    { name: "Cranston", zips: "02905, 02906, 02907, 02910, 02920, 02921" },
    { name: "Warwick", zips: "02886, 02887, 02888, 02889, 02818" },
    { name: "Pawtucket", zips: "02860, 02861, 02862" },
    { name: "East Providence", zips: "02914, 02915, 02916" },
    { name: "North Providence", zips: "02904, 02911" },
    { name: "Johnston", zips: "02919" },
    { name: "Cumberland", zips: "02864, 02827" },
    { name: "Woonsocket", zips: "02895" },
    { name: "Coventry", zips: "02816, 02827" },
    { name: "West Warwick", zips: "02893" },
    { name: "Bristol", zips: "02809" },
    { name: "Barrington", zips: "02806" },
    { name: "Newport", zips: "02840, 02841" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Providence Emergency Processing", desc: "Reserved courier access to the Boston Passport Agency — as fast as 24 hours for Providence-area travelers with immediate departures." },
    { icon: "ri-graduation-cap-line", title: "Brown + RISD + JWU Student Expertise", desc: "Thousands of Providence college students study abroad each year. We guide Brown, RISD, Johnson & Wales, and Providence College students through first-time passports and renewals before program deadlines." },
    { icon: "ri-government-line", title: "State Government & NGO Ready", desc: "Rhode Island state employees, nonprofit professionals, and academic researchers with short-notice international travel — we handle the passport while you handle the mission." },
    { icon: "ri-building-2-line", title: "Creative Economy & Family Expedited", desc: "RISD artists heading to European exhibitions, Federal Hill families booking first international trips — we guide everyone through with personal attention." },
  ],
  internalLinks: [
    { name: "Fast passport services in Boston, MA", route: "/boston" },
    { name: "Expedited passport in Warwick, RI", route: "/warwick" },
    { name: "Quick passport services in Cranston, RI", route: "/cranston" },
    { name: "Urgent passport services in Pawtucket, RI", route: "/pawtucket" },
    { name: "Rhode Island passport services overview", route: "/rhode-island" },
  ],
  fpcOfficeAddress: "One Boston Place, Suite 2600, Boston, MA 02108",
  fpcOfficeNote: "Fast Passport Center serves Providence via secure registered courier with direct access to the Boston Passport Agency at 10 Causeway St. Our registered couriers hand-deliver your documents — meaning you get faster results without the appointment wait. Providence residents can ship from any UPS Store, FedEx Office, or post office location across the city.",
  fpcOfficeDistance: "Serving Providence, Cranston, Warwick, Pawtucket, East Providence, and all Rhode Island communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Providence and the greater Rhode Island region. From the downtown business district to College Hill and the East Side, from Federal Hill to Olneyville and Elmwood — we serve all of Providence County and beyond. Our courier service eliminates the need to drive to Boston and wait for a passport agency appointment.",
  nearbyCitiesIntro: "We serve Providence and all surrounding Rhode Island communities including Cranston, Warwick, Pawtucket, East Providence, North Providence, Johnston, Cumberland, Woonsocket, Coventry, West Warwick, Bristol, and Newport. We also serve travelers departing from T.F. Green Airport and Logan International who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Providence as simple as possible — with the efficiency and reliability Rhode Island state employees, university students, and families need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Providence travelers start online and ship documents overnight — no office visit required for most services.",
  mistakesIntro: "Providence travelers encounter unique passport issues — everything from student study-abroad deadlines to last-minute state government travel to 6-month validity rules for European destinations. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Providence travelers — from Brown and RISD students to State House professionals to Federal Hill families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Providence, RI? Here are the most common ones we hear from Rhode Island-area travelers.",
  statePageRoute: "/states/rhode-island",
  statePageName: "Rhode Island",
  servicesIntro: "Whether you need a renewal before a Logan flight to London, an emergency passport for a last-minute state government trip, or a first-time passport for a RISD study-abroad program — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://dmv.ri.gov",
  dmvLabel: "Rhode Island DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Cannot upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Logan flight to London in two days? State House business in Brussels this week? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for Providence-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Providence residents renew fast — no agency visit required for most renewals. Perfect for downtown professionals, Brown faculty, and Rhode Island families preparing for international travel.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Providence residents through every step — from College Hill to Federal Hill to Olneyville — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your RISD exhibition trip or Brown research travel is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family vacation or Rhode Island family trip abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations. Common for Providence professionals after name changes.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for Providence state government employees, Brown researchers, and nonprofit professionals who travel internationally while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Providence-area travelers through the process so your international itinerary or academic travel stays on track.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. We expedite passport card applications with the same courier service as book passports.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Providence RI", description: "Urgent passport processing for Providence Rhode Island travelers with immediate departure needs from Logan or T.F. Green." },
    { name: "Expedited Passport Renewal Providence RI", description: "Fast passport renewal for Providence Rhode Island residents with a previously-issued passport." },
    { name: "First-Time Passport Providence RI", description: "New passport application services for Providence Rhode Island residents applying for their first passport." },
    { name: "Child Passport Providence RI", description: "Minor passport services for Providence Rhode Island families with children under 16." },
    { name: "Name Change Passport Providence RI", description: "Passport name correction after marriage, divorce, or legal name changes for Providence residents." },
    { name: "Second Passport Providence RI", description: "Second passport services for Providence state government employees, Brown researchers, and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Sarah M.",
      location: "College Hill, Providence",
      rating: 5,
      text: "My daughter got into RISD's European honors program with just 10 days before departure — and her passport was expired. Fast Passport Center got it back in 48 hours. The document review caught a photo issue we never would have noticed. She made her flight.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "The Patel Family",
      location: "Federal Hill, Providence",
      rating: 5,
      text: "We booked a last-minute trip to visit family in India and realized our son's passport expired two months ago. They walked us through everything — the child passport process was way simpler than we expected — and it arrived in 5 days.",
      service: "Child Passport Renewal",
    },
    {
      name: "David L.",
      location: "East Side, Providence",
      rating: 5,
      text: "State House business trip to Brussels came up with one week's notice. I needed a name change on my passport after getting married. They handled everything remotely and the corrected passport was here before I packed.",
      service: "Name Change Passport",
    },
    {
      name: "Prof. Martinez",
      location: "Brown University, Providence",
      rating: 5,
      text: "Second time using Fast Passport Center for research travel to archives in Europe. They know exactly what academic travelers need and the timeline is always accurate. The dedicated agent model is fantastic — same person follows your case the whole way.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "prov-1",
      name: "Providence Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "24 Corliss St",
      city: "Providence",
      state: "RI",
      zip: "02904",
      phone: "(401) 276-2400",
      hours: "Mon–Fri 8:00 AM – 4:30 PM (appointment required)",
      distance: "Downtown Providence",
      website: "https://tools.usps.com/find-location.htm?location=1368763",
      websiteLabel: "USPS Location",
      notes: "Main Providence post office, passport acceptance services by appointment. Photo services on-site. Busiest facility in the city — book early.",
      icon: "ri-mail-line",
    },
    {
      id: "prov-2",
      name: "Providence City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "25 Dorrance St",
      city: "Providence",
      state: "RI",
      zip: "02903",
      phone: "(401) 421-7740",
      hours: "Mon–Fri 8:30 AM – 4:30 PM (appointment required)",
      distance: "Downtown Providence — City Hall",
      website: "https://www.providenceri.gov/city-clerk/",
      websiteLabel: "Providence City Clerk",
      notes: "Convenient for downtown workers and residents. Passport acceptance services by appointment. City Hall location near Kennedy Plaza.",
      icon: "ri-government-line",
    },
    {
      id: "prov-3",
      name: "East Side Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "351 Wickenden St",
      city: "Providence",
      state: "RI",
      zip: "02903",
      phone: "(401) 331-1234",
      hours: "Mon–Fri 9:00 AM – 4:00 PM, walk-ins available",
      distance: "College Hill / East Side",
      website: "https://tools.usps.com/find-location.htm?location=1368781",
      websiteLabel: "USPS Location",
      notes: "Convenient for Brown and RISD students on College Hill. Walk-in hours subject to clerk availability — call ahead during peak study-abroad season.",
      icon: "ri-mail-line",
    },
    {
      id: "prov-4",
      name: "Cranston Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1055 Park Ave",
      city: "Cranston",
      state: "RI",
      zip: "02910",
      phone: "(401) 941-4340",
      hours: "Mon–Fri 8:00 AM – 4:30 PM, Sat 9:00 AM – 12:00 PM (appointment required)",
      distance: "Cranston — bordering Providence",
      website: "https://tools.usps.com/find-location.htm?location=1387611",
      websiteLabel: "USPS Location",
      notes: "Serves Cranston and southern Providence neighborhoods. Saturday appointments available — rare for Rhode Island facilities. Passport photos available.",
      icon: "ri-mail-line",
    },
    {
      id: "prov-5",
      name: "RI Secretary of State — Document Services",
      type: "State Office / Document Services",
      address: "148 W River St",
      city: "Providence",
      state: "RI",
      zip: "02904",
      phone: "(401) 222-2357",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "Downtown Providence",
      website: "https://www.sos.ri.gov/",
      websiteLabel: "RI Secretary of State",
      notes: "State-level document services for vital records and apostilles. For passport applications specifically, visit an authorized acceptance facility or post office.",
      icon: "ri-file-shield-line",
    },
    {
      id: "prov-6",
      name: "Warwick Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "575 Sandy Ln",
      city: "Warwick",
      state: "RI",
      zip: "02889",
      phone: "(401) 737-8700",
      hours: "Mon–Fri 8:00 AM – 4:30 PM, Sat 9:00 AM – 12:00 PM (appointment required)",
      distance: "Warwick — near T.F. Green Airport",
      website: "https://tools.usps.com/find-location.htm?location=1378552",
      websiteLabel: "USPS Location",
      notes: "Convenient for Warwick, Cranston, and southern Providence residents. Near T.F. Green Airport — good for last-minute pre-flight passport needs.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive to Boston Passport Agency (50+ miles from Providence)", fpc: "Most Providence travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Providence-Specific Guidance", agency: "Generic — no university, state government, or creative economy expertise", fpc: "Full Providence/Brown/RISD/state government knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming the Boston Agency Takes Walk-Ins From Providence",
      description: "The Boston Passport Agency requires appointments even for emergencies. Many Providence residents drive 50 miles to Boston only to be turned away. Our courier channel bypasses this entirely.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Using DS-82 When DS-11 Is Required",
      description: "Providence applicants — especially college students — often attempt the simpler renewal form when their passport was issued at age 15 or younger. A rejected form costs 1–2 weeks. We review before you submit.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations at Local Pharmacies",
      description: "Many Providence CVS and Walgreens locations produce photos that fail State Department standards — wrong head size, shadows, or off-white backgrounds. We catch this in review before it causes a rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for University-Age Minors",
      description: "Brown and RISD have students under 18 starting study-abroad programs. Families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Not Accounting for the 6-Month Passport Validity Rule",
      description: "Providence travelers booking to Italy, France, the UK, or other European destinations often do not know their passport must have 6 months remaining beyond the return date. We flag this immediately.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or dorm room." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Providence clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive to Boston or wait at the Boston Passport Agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Providence?", a: "We offer Providence-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Do I need to visit a Providence office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Providence post office or city clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Providence address." },
    { q: "Where can I get a passport acceptance in Providence?", a: "Providence has acceptance facilities at the Main Post Office on Corliss Street, the City Clerk at City Hall, the East Side Post Office on Wickenden Street, and nearby facilities in Cranston and Warwick. We maintain an updated list of Rhode Island-area facilities with hours, photo availability, and appointment options." },
    { q: "Does the Boston Passport Agency handle walk-ins from Providence?", a: "The Boston Passport Agency requires appointments for life-or-death emergency travel only. Walk-ins are rarely accepted. For travel within 3–14 days, our registered courier service is the fastest legally available channel without driving to Boston and competing for an agency appointment." },
    { q: "What is the best price for expedited passport service in Providence?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "What documents do Providence applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a Providence-specific checklist." },
    { q: "Can I get a passport photo in Providence?", a: "Yes — many Providence CVS, Walgreens, UPS Stores, and FedEx Office locations offer passport photos. We recommend confirming photo standards compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
    { q: "How do I check my Providence passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Providence to final return shipping typically matches your chosen speed tier." },
    { q: "Can you help with passports for Brown or RISD study abroad?", a: "Absolutely. We regularly assist Brown, RISD, Johnson & Wales, and Providence College students with expedited passports for study-abroad programs. We understand the acceptance-to-departure timeline and coordinate around program orientation dates." },
    { q: "Is Providence near a passport agency?", a: "The nearest passport agency is in Boston — approximately 50 miles from Providence. However, it only serves travelers with life-or-death emergencies. For standard expedited needs — even urgent ones — our courier channel is faster and more accessible than driving to Boston for an agency appointment." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Providence Residents",
    intro: "Providence's unique position as Rhode Island's capital, an Ivy League university hub, and the creative heart of New England creates distinct passport timing challenges. Here's what Providence travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "BOS — Logan International Airport",
        subtitle: "Boston Logan, primary international hub for Rhode Island",
        description: "Logan handles more than 42 million passengers annually with direct flights to London, Paris, Dublin, Tokyo, and 80+ international destinations. Providence travelers — a quick 50-mile drive or Amtrak ride from downtown — rely on Logan for virtually all international travel. The airport's global reach creates regular passport urgencies: state government officials with last-minute EU travel, Brown faculty heading to conferences, and students scrambling before study-abroad deadlines.",
        tip: "Providence-to-Boston is an easy drive until there's traffic on I-95 — and there's always traffic on I-95. Factor in the drive time when calculating your passport appointment window, or better yet, avoid the agency visit entirely with our courier service.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PVD — T.F. Green Airport",
        subtitle: "Warwick, RI — Rhode Island's own airport",
        description: "T.F. Green in Warwick is just 10 miles from downtown Providence and serves domestic routes plus limited international connections. Many Providence residents use PVD for domestic flights to international gateways like JFK, Newark, or Philadelphia. The convenience of a local airport sometimes creates a false sense of flexibility — a missed connection due to a passport issue at a domestic airport can cascade quickly and strand you.",
        tip: "Even if you are connecting through another U.S. hub to fly internationally, you still need a valid passport at your point of departure in Providence or Warwick.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "University Study Abroad & Student Travel",
        subtitle: "Brown University, RISD, Johnson & Wales, Providence College",
        description: "Providence has one of the highest concentrations of college students per capita of any U.S. city. Brown University and RISD alone send thousands of students abroad each year for semester exchange, summer programs, and studio intensives in Rome, London, and Paris. Johnson & Wales culinary students travel for international externships. The acceptance-to-orientation pipeline is often 6–10 weeks, requiring expedited processing to meet program start dates. Many of these students — especially international students and first-generation college students — have never held U.S. passports.",
        tip: "First-time applicants in Providence must visit an acceptance facility in person. We guide students through the nearest facility with the earliest available appointment, so they do not miss their program orientation.",
      },
      {
        icon: "ri-government-line",
        title: "State Government & Nonprofit Travel",
        subtitle: "Rhode Island State House, NGOs, academic research",
        description: "As Rhode Island's capital, Providence hosts the State House and numerous state agencies. Government employees, nonprofit professionals, and university researchers frequently travel internationally — to EU summits, global health conferences, and academic collaborations — on short timelines. When a grant-funded research trip or a state delegation visit emerges with 1–2 weeks notice, standard post office passport processing cannot keep up. The Boston Passport Agency is 50 miles away and requires appointments booked weeks in advance.",
        tip: "State and nonprofit travel often has fixed, unmovable dates tied to conferences, summits, or funding cycles. We recommend starting the passport process the moment your travel is confirmed — not when you start packing.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Providence & the Creative Economy Abroad",
      description: "Providence's identity as a creative capital — anchored by RISD, AS220, and a thriving arts scene — means a steady stream of artists, designers, and makers traveling internationally for exhibitions, residencies, and creative collaborations. These travelers often have irregular income, short-notice opportunities, and complex travel logistics. We have helped RISD graduates get passports in 48 hours for last-minute gallery openings in Berlin and Milan.",
    },
  },
  destinations: {
    title: "Where Providence Travelers Go — And Why Passports Get Urgent",
    intro: "Providence's academic, government, and creative sectors create unique international travel patterns. Here are the most common destinations and the urgency patterns we see.",
    stats: [
      { stat: "38%", label: "of Providence expedited requests are university-related — study abroad, research, or academic conferences" },
      { stat: "2–4 Weeks", label: "average lead time Providence travelers have from trip booking to international departure" },
      { stat: "Jan–Mar", label: "peak season when spring semester study abroad, Q1 government travel, and creative residencies collide" },
      { stat: "48 Hours", label: "shortest turnaround we have accommodated for a RISD student departing to a Milan exhibition" },
    ],
    destinations: [
      { name: "Italy", context: "RISD European Honors Program in Rome, Brown semester abroad in Bologna, Johnson & Wales culinary externships", urgency: "Study-abroad deadlines cluster in January and August", icon: "ri-map-pin-2-line" },
      { name: "United Kingdom", context: "Brown semester programs in London, academic conferences, family heritage trips, state trade missions", urgency: "Academic travel and government delegations booked 2–3 weeks out", icon: "ri-map-pin-2-line" },
      { name: "France", context: "RISD and Brown programs in Paris, arts residencies, creative collaborations, design conferences", urgency: "Creative residencies often have 1–2 week confirmation-to-departure windows", icon: "ri-map-pin-2-line" },
      { name: "Portugal & Cape Verde", context: "Large Portuguese and Cape Verdean diaspora in Providence and across Rhode Island — family heritage travel, community events", urgency: "Family visits booked around holidays and community festivals with fixed dates", icon: "ri-map-pin-2-line" },
      { name: "Germany", context: "Brown research collaborations, design and manufacturing partnerships, academic conferences in Berlin and Munich", urgency: "Conference dates are fixed and unmovable — 2–3 week typical booking window", icon: "ri-map-pin-2-line" },
      { name: "Dominican Republic", context: "Strong Dominican community in Providence, family visits, medical mission trips, and community service travel", urgency: "Mission trips and family reunions booked 3–4 weeks out during school breaks", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Rhode Island Service Areas",
    crossLinksDesc: "We serve travelers across the Ocean State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Warwick", route: "/warwick" },
      { name: "Cranston", route: "/cranston" },
      { name: "Pawtucket", route: "/pawtucket" },
    ],
  },
  photoLocations: [
    { name: "CVS Pharmacy — Downtown Providence", address: "799 Hope St, Providence, RI 02906", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — College Hill", address: "533 North Main St, Providence, RI 02904", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "CVS Pharmacy — Federal Hill", address: "799 Hope St, Providence, RI 02906", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "The UPS Store — Wayland Square", address: "203 Wayland Ave, Providence, RI 02906", hours: "Mon–Fri 9 AM – 7 PM, Sat 10 AM – 4 PM", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walgreens — East Side", address: "1140 North Main St, Providence, RI 02904", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — Downtown", address: "1 Providence Pl, Providence, RI 02903", hours: "Mon–Fri 8 AM – 9 PM, Sat–Sun 10 AM – 6 PM", website: "https://www.office.fedex.com/default/office" },
    { name: "Walmart — Cranston", address: "1776 Plainfield Pike, Cranston, RI 02921", hours: "Daily 7 AM – 10 PM", website: "https://www.walmart.com/cp/photo/1078904" },
    { name: "CVS Pharmacy — Cranston", address: "2000 Broad St, Cranston, RI 02905", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
  ],
  dmvLocations: [
    {
      name: "RI DMV — Cranston",
      address: "600 New London Ave, Cranston, RI 02920",
      hours: "Mon–Fri, 8:30 AM – 3:15 PM",
      note: "Main Rhode Island DMV office — driver licenses, REAL ID, vehicle registration. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "AAA Providence",
      address: "1512 Park Ave, Cranston, RI 02920",
      hours: "Mon–Fri, 9:00 AM – 5:00 PM, Sat 9:00 AM – 1:00 PM",
      note: "AAA members can access limited DMV services including license renewals and REAL ID. Check availability before visiting.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PVD",
      name: "T.F. Green Airport",
      distance: "About 10 miles from downtown Providence",
      address: "2000 Post Rd, Warwick, RI 02886",
      note: "Rhode Island's primary airport serving domestic destinations and limited international connections. Convenient for Providence and all Rhode Island residents.",
      flightUrl: "https://www.pvdairport.com/",
      flightLabel: "T.F. Green Airport Website",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "About 50 miles from downtown Providence",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and worldwide destinations. Providence travelers primarily use Logan for international travel — accessible via I-95 or Amtrak.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Rhode Island Department of Health. Online, mail, or in-person options available for Providence County residents.",
      url: "https://health.ri.gov/records/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through the RI Department of Health Vital Records office. Required for estate and legal matters in Providence County.",
      url: "https://health.ri.gov/records/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Providence City Clerk. Certified copies available for name changes and legal purposes across Rhode Island.",
      url: "https://www.providenceri.gov/city-clerk/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Rhode Island Family Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.courts.ri.gov/family/",
    },
  ],
};

export default providenceConfig;